import { MarchVoucher } from 'components/common/MarchPromo/types'
import { createContext, useEffect, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { useApp } from 'store/steps'
import useTicketTranslations from 'utils/hooks/useTicketTranslations'
import { useGuestsCart } from './useGuestsCart'

export const GuestsDataContext = createContext<{
    marchVouchersData: MarchVoucher[]
    totalPrice?: number
    voucherPrice?: number
    isLoading?: boolean
    setMarchVouchersData: (value: MarchVoucher[]) => void
    setTotalPrice: (value: number) => void
    setVoucherPrice: (value: number) => void
    setFormData: (shouldSetInitialData: boolean) => void
    setIsLoading: (value: boolean) => void
}>({
    marchVouchersData: [],
    totalPrice: undefined,
    voucherPrice: undefined,
    isLoading: false,
    setMarchVouchersData: () => null,
    setTotalPrice: () => null,
    setVoucherPrice: () => null,
    setFormData: () => null,
    setIsLoading: () => null,
})

export const GuestsDataProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const [marchVouchersData, setMarchVouchersData] = useState<MarchVoucher[]>(
        []
    )
    const [totalPrice, setTotalPrice] = useState<number | undefined>()
    const [voucherPrice, setVoucherPrice] = useState<number | undefined>()
    const [isLoading, setIsLoading] = useState(true)

    const { deleteItemsFromCart } = useGuestsCart()
    const { isCreatorMode } = useApp()
    const {
        getTicketPersonByKey,
        getTicketZoneByKey,
        getTicketTypeByKey,
        areTranslationsReady,
    } = useTicketTranslations()

    const { tickets: selfcareCartTickets } = useAppSelector(
        (state) => state.selfcare
    )
    const {
        items: { marchVouchers: selfcareCartVouchers },
    } = useAppSelector((state) => state.selfcareCart)
    const {
        items: {
            tickets: creatorCartTickets,
            marchVouchers: creatorCartVouchers,
        },
    } = useAppSelector((state) => state.cart)
    const { ticketBoxes } = useAppSelector((state) => state.ticket)

    // TODO: consider separating the two cases below:

    // setting initial data for selfcare
    useEffect(() => {
        // wait for data for translations and for currently edited order
        if (
            isCreatorMode ||
            !areTranslationsReady ||
            !selfcareCartTickets?.length
        )
            return

        // promotion do not applies to tickets for infants
        const availableTickets = selfcareCartTickets?.filter(
            ({
                original: {
                    detail: { person },
                },
            }) => person !== 'kid_to_90_cm'
        )

        // collect unique boxIds from tickets within current order
        const boxIds =
            availableTickets
                ?.map(
                    ({
                        original: {
                            detail: { boxId },
                        },
                    }) => boxId
                )
                .reduce((accumulator, currentId) => {
                    if (currentId && !accumulator?.includes(currentId)) {
                        accumulator.push(currentId)
                    }
                    return accumulator
                }, [] as string[]) || []

        const vouchersData: MarchVoucher[] = boxIds.map((boxId) => {
            // tickets with the same current boxId
            const ticketsByBoxId =
                availableTickets
                    ?.filter(({ original: { detail } }) => {
                        const { boxId: currentTicketBoxId } = detail
                        return currentTicketBoxId === boxId
                    })
                    ?.map(({ original: { detail } }) => {
                        const { cartItemId, person, zone, ticketTime, day } =
                            detail

                        return {
                            ticketId: cartItemId,
                            date: day,
                            person: getTicketPersonByKey(person) || '',
                            zone: getTicketZoneByKey(zone) || '',
                            ticketType: getTicketTypeByKey(ticketTime) || '',
                        }
                    }) || []
            // voucher from cart connected with the tickets filtered above (if there are vouchers in cart)
            const voucherFromCart = selfcareCartVouchers.find(
                ({ detail }) =>
                    detail.cartItemId === ticketsByBoxId[0]?.ticketId
            )

            // if in cart there is voucher connected with filterd tickets...
            if (voucherFromCart) {
                const { productId, unitPrice, detail } = voucherFromCart
                // ...populate local vouchers data with details from cart
                return {
                    firstName: detail.name,
                    lastName: detail.surname,
                    price: unitPrice,
                    productId,
                    boxId,
                    tickets: [...ticketsByBoxId],
                }
            }
            // ...or set generic initial data
            return {
                firstName: undefined,
                lastName: undefined,
                price: undefined,
                productId: undefined,
                boxId,
                tickets: [...ticketsByBoxId],
            }
        })

        if (vouchersData.length === 0) return

        const voucherPrice = vouchersData[0].price || 0
        const totalPrice =
            vouchersData
                .map(({ tickets }) => tickets.length)
                .reduce(
                    (accumulator, currentValue) => accumulator + currentValue,
                    0
                ) * voucherPrice

        // finally set data for context
        setMarchVouchersData(vouchersData)
        setVoucherPrice(voucherPrice)
        setTotalPrice(totalPrice)
    }, [areTranslationsReady, selfcareCartTickets, selfcareCartVouchers])

    const setFormData = (shouldSetInitialData: boolean) => {
        // wait for data for translations and for park tickets
        if (
            !isCreatorMode ||
            !areTranslationsReady ||
            creatorCartTickets.length === 0
        )
            return

        // check for any applicable tickets,
        // promotion do not applies to:
        // - tickets for infants
        // - bundled tickets, e.g. senior package
        const availableTickets = creatorCartTickets?.filter(
            ({ itemsBundle, detail: { person } }) =>
                person !== 'kid_to_90_cm' && !itemsBundle?.length
        )
        if (availableTickets.length === 0) return

        // skip if we do not want inital data
        // and we are in the middle of editing
        if (
            !shouldSetInitialData &&
            creatorCartVouchers.length === 0 &&
            voucherPrice !== undefined
        )
            return

        // ...else, restore march promo vouchers data from cart:

        // collect unique boxIds from tickets already in cart
        const boxIds =
            availableTickets
                ?.map(({ detail: { boxId } }) => boxId)
                .reduce((accumulator, currentId) => {
                    if (currentId && !accumulator?.includes(currentId)) {
                        accumulator.push(currentId)
                    }
                    return accumulator
                }, [] as string[]) || []

        const vouchersData: MarchVoucher[] = boxIds.map((boxId) => {
            // tickets with the same current boxId
            const ticketsByBoxId =
                availableTickets
                    ?.filter(({ detail }) => {
                        const { boxId: currentTicketBoxId } = detail
                        return currentTicketBoxId === boxId
                    })
                    ?.map(({ cartItemId, detail }) => {
                        const { person, zone, ticketTime, date } = detail

                        return {
                            ticketId: cartItemId,
                            date,
                            person: getTicketPersonByKey(person) || '',
                            zone: getTicketZoneByKey(zone) || '',
                            ticketType: getTicketTypeByKey(ticketTime) || '',
                        }
                    }) || []
            // voucher from cart connected with the tickets filtered above
            const voucherFromCart = creatorCartVouchers.find(
                ({ detail }) =>
                    detail.cartItemId === ticketsByBoxId[0]?.ticketId
            )

            // if in cart there is voucher connected with filterd tickets
            // and we do not want initial data...
            if (!shouldSetInitialData && voucherFromCart) {
                const { productId, unitPrice, detail } = voucherFromCart
                // ...populate local vouchers data with details from cart
                return {
                    firstName: detail.name,
                    lastName: detail.surname,
                    price: unitPrice,
                    productId,
                    boxId,
                    tickets: [...ticketsByBoxId],
                }
            }
            // ...or set generic initial data
            return {
                firstName: undefined,
                lastName: undefined,
                price: undefined,
                productId: undefined,
                boxId,
                tickets: [...ticketsByBoxId],
            }
        })

        if (vouchersData.length === 0) return

        const commonVoucherPrice = vouchersData[0].price || 0
        const totalPrice =
            vouchersData
                .map(({ tickets }) => tickets.length)
                .reduce(
                    (accumulator, currentValue) => accumulator + currentValue,
                    0
                ) * commonVoucherPrice

        // finally set data for context
        setMarchVouchersData(vouchersData)
        setVoucherPrice(shouldSetInitialData ? undefined : commonVoucherPrice)
        setTotalPrice(totalPrice)
    }

    // setting data for creator based on changes in cart
    useEffect(() => {
        setFormData(false)
    }, [areTranslationsReady, creatorCartTickets, creatorCartVouchers])

    useEffect(() => {
        const availableTicketsIds = creatorCartTickets
            ?.filter(
                ({ itemsBundle, detail: { person } }) =>
                    person !== 'kid_to_90_cm' && !itemsBundle?.length
            )
            .map(({ cartItemId }) => cartItemId)
            .filter((id) => !!id)
            .sort()
            .join()
        const marchVouchersIdsByCart = creatorCartVouchers
            .map(({ detail: { cartItemId } }) => cartItemId)
            .sort()
            .join()
        const marchVouchersTicketsByLocal = marchVouchersData
            .map(({ tickets }) => tickets)
            .flat()
        const marchVouchersIdsByLocal = marchVouchersTicketsByLocal
            .map(({ ticketId }) => ticketId)
            .sort()
            .join()

        if (availableTicketsIds !== marchVouchersIdsByLocal) {
            // delete when adding new tickets with vouchers in cart
            if (marchVouchersIdsByCart && marchVouchersIdsByLocal)
                deleteItemsFromCart()
            // or just reset form without vouchers in cart
            else setFormData(true)
        } else if (
            (availableTicketsIds !== marchVouchersIdsByCart &&
                !!marchVouchersIdsByCart) ||
            availableTicketsIds !== marchVouchersIdsByLocal
        ) {
            // before expected deleting because of differences in ids
            setIsLoading(true)
        } else if (
            marchVouchersTicketsByLocal.length === creatorCartVouchers.length ||
            (marchVouchersData.length !== 0 && creatorCartVouchers.length === 0)
        )
            // hide loader after verifying data
            setIsLoading(false)
    }, [
        creatorCartTickets,
        creatorCartVouchers,
        marchVouchersData,
        ticketBoxes,
    ])

    return (
        <GuestsDataContext.Provider
            value={{
                marchVouchersData,
                totalPrice,
                voucherPrice,
                isLoading,
                setMarchVouchersData,
                setTotalPrice,
                setVoucherPrice,
                setFormData,
                setIsLoading,
            }}
        >
            {children}
        </GuestsDataContext.Provider>
    )
}
